// If theme is modified re-run shared:wui:chakra:typegen to update the
// types for chakra components
import { createSystem, defaultConfig, defineConfig } from "@chakra-ui/react";
import {
    colors,
    focusRing,
    fontSizes,
    semanticShadows,
    textStyles,
} from "./consts";
import { buttonRecipe } from "./recipes/button.recipe";
import { drawerRecipe } from "./recipes/drawer.recipe";
import { choiceSlotRecipe } from "./slot-recipes/choice.recipe";

interface ThemeObject {
    [key: string]: string | number | { [key: string]: string | number };
}

interface TransformedThemeObject<T extends string | number = string | number> {
    [key: string]: { value: T } | { [key: string]: { value: T } };
}

export type TokenNode = { value: string | number };

export type TokenScale = Record<string, TokenNode>;

export type ColorsInput = Record<string, TokenScale | TokenNode>;

export type Variant =
    | "solid"
    | "contrast"
    | "fg"
    | "muted"
    | "subtle"
    | "emphasized"
    | "focusRing";

export type SemanticEntry = Record<Variant, { value: string }>;

export type SemanticColorsOutput = Record<string, SemanticEntry>;

const VARIANT_TO_STEP: Record<Variant, string> = {
    solid: "500",
    contrast: "100",
    fg: "700",
    muted: "100",
    subtle: "200",
    emphasized: "300",
    focusRing: "500",
};

function isTokenNode(x: unknown): x is TokenNode {
    return (
        typeof x === "object" &&
        x !== null &&
        "value" in x &&
        typeof x.value === "string"
    );
}

function isTokenScale(x: unknown): x is TokenScale {
    if (typeof x !== "object" || x === null) return false;
    if (isTokenNode(x)) return false;
    for (const k of Object.keys(x as Record<string, unknown>)) {
        const v = (x as Record<string, unknown>)[k];
        if (!isTokenNode(v)) return false;
    }
    return true;
}

function pickNearestStep(steps: string[], desired: string): string {
    if (steps.includes(desired)) return desired;
    const nums = steps
        .map(k => Number(k))
        .filter(n => !Number.isNaN(n))
        .sort((a, b) => a - b);
    if (nums.length === 0) return steps[0] ?? desired;
    const target = Number(desired);
    let best = nums[0];
    let bestD = Math.abs(nums[0] - target);
    for (let i = 1; i < nums.length; i++) {
        const d = Math.abs(nums[i] - target);
        if (d < bestD) {
            best = nums[i];
            bestD = d;
        }
    }
    return String(best);
}

export function buildAllSemanticRefs(
    colors: ColorsInput,
): SemanticColorsOutput {
    const out: SemanticColorsOutput = {};

    for (const name of Object.keys(colors)) {
        const node = colors[name];

        if (isTokenNode(node)) {
            const ref = `{colors.${name}}`; // change to `{colors.${name}.value}` if required by your system
            const entry = {} as SemanticEntry;
            (Object.keys(VARIANT_TO_STEP) as Variant[]).forEach(v => {
                entry[v] = { value: ref };
            });
            out[name] = entry;
            continue;
        }

        if (isTokenScale(node)) {
            const steps = Object.keys(node);
            const entry = {} as SemanticEntry;
            (Object.keys(VARIANT_TO_STEP) as Variant[]).forEach(v => {
                const desired = VARIANT_TO_STEP[v];
                const step = pickNearestStep(steps, desired);
                entry[v] = { value: `{colors.${name}.${step}}` };
            });
            out[name] = entry;
            continue;
        }

        const fallbackRef = `{colors.${name}}`;
        const entry = {} as SemanticEntry;
        (Object.keys(VARIANT_TO_STEP) as Variant[]).forEach(v => {
            entry[v] = { value: fallbackRef };
        });
        out[name] = entry;
    }

    return out;
}

export function transformThemeObject(
    dataObject: ThemeObject,
): TransformedThemeObject {
    const transformed: TransformedThemeObject = {};

    for (const key in dataObject) {
        if (
            typeof dataObject[key] === "string" ||
            typeof dataObject[key] === "number"
        ) {
            transformed[key] = { value: dataObject[key] };
        } else if (
            typeof dataObject[key] === "object" &&
            dataObject[key] !== null
        ) {
            const nested: { [key: string]: { value: string | number } } = {};
            for (const nestedKey in dataObject[key]) {
                nested[nestedKey] = {
                    value: (
                        dataObject[key] as { [key: string]: string | number }
                    )[nestedKey],
                };
            }
            transformed[key] = nested;
        }
    }

    return transformed;
}

const fonts = {
    heading: "Inter, sans-serif",
    body: "Inter, sans-serif",
};

const fontWeights = {
    hairline: 100,
    thin: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
    extraBlack: 950,
};

export const transformedColors = transformThemeObject({
    ...colors,
    gray: colors.neutralGrey,
});
export const transformedFontSizes = transformThemeObject(fontSizes);
export const transformedFonts = transformThemeObject(fonts) as Record<
    string,
    { value: string }
>;
export const transformedFontWeights = transformThemeObject(fontWeights);
console.log("transformedColors::", transformedColors);
const semanticColor = buildAllSemanticRefs(transformedColors);

console.log("semantic color::", semanticColor);
const config = defineConfig({
    theme: {
        tokens: {
            colors: {
                ...transformedColors,
                bg: { value: "#f7f7f7" },
            },
            fonts: {
                ...transformedFonts,
            },
            fontSizes: {
                ...transformedFontSizes,
            },
            fontWeights: {
                ...transformedFontWeights,
            },
        },
        semanticTokens: {
            colors: semanticColor,
            shadows: {
                ...semanticShadows,
                ...focusRing,
            },
        },
        recipes: {
            button: buttonRecipe,
            drawer: drawerRecipe,
        },
        slotRecipes: {
            checkbox: choiceSlotRecipe,
            radioGroup: choiceSlotRecipe,
            switch: choiceSlotRecipe,
        },
        textStyles,
    },
});

export const system = createSystem(defaultConfig, config);
